import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Home as HomeIcon, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [activeTab, setActiveTab] = useState('properties');
  const [propertyForm, setPropertyForm] = useState({
    title: '',
    location: 'Mumbai',
    property_type: 'Villa',
    price: '',
    bedrooms: 3,
    bathrooms: 2,
    area_sqft: 2000,
    description: '',
    features: '',
    images: '',
    status: 'available',
    featured: false,
  });

  const [blogForm, setBlogForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: 'Venara Estate',
    category: 'Market Insights',
    image_url: '',
  });

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...propertyForm,
        features: propertyForm.features.split(',').map(f => f.trim()).filter(Boolean),
        images: propertyForm.images.split(',').map(img => img.trim()).filter(Boolean),
        bedrooms: parseInt(propertyForm.bedrooms),
        bathrooms: parseInt(propertyForm.bathrooms),
        area_sqft: parseInt(propertyForm.area_sqft),
      };

      await axios.post(`${API}/properties`, payload);
      toast.success('Property added successfully!');
      
      // Reset form
      setPropertyForm({
        title: '',
        location: 'Mumbai',
        property_type: 'Villa',
        price: '',
        bedrooms: 3,
        bathrooms: 2,
        area_sqft: 2000,
        description: '',
        features: '',
        images: '',
        status: 'available',
        featured: false,
      });
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error('Failed to add property');
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/blog`, blogForm);
      toast.success('Blog post added successfully!');
      
      // Reset form
      setBlogForm({
        title: '',
        excerpt: '',
        content: '',
        author: 'Venara Estate',
        category: 'Market Insights',
        image_url: '',
      });
    } catch (error) {
      console.error('Error adding blog post:', error);
      toast.error('Failed to add blog post');
    }
  };

  return (
    <div className="min-h-screen bg-background py-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="font-playfair text-4xl lg:text-5xl font-bold text-primary mb-4">
            Admin Panel
          </h1>
          <p className="text-muted-foreground">Manage properties and blog posts</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-muted">
          <button
            onClick={() => setActiveTab('properties')}
            data-testid="tab-properties"
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors duration-300 ${
              activeTab === 'properties'
                ? 'text-secondary border-b-2 border-secondary'
                : 'text-muted-foreground hover:text-primary'
            }`}
          >
            <HomeIcon size={20} />
            Add Property
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            data-testid="tab-blog"
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors duration-300 ${
              activeTab === 'blog'
                ? 'text-secondary border-b-2 border-secondary'
                : 'text-muted-foreground hover:text-primary'
            }`}
          >
            <FileText size={20} />
            Add Blog Post
          </button>
        </div>

        {/* Property Form */}
        {activeTab === 'properties' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <form onSubmit={handlePropertySubmit} className="bg-white p-8 border border-muted" data-testid="property-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Property Title *</label>
                  <input
                    type="text"
                    required
                    value={propertyForm.title}
                    onChange={(e) => setPropertyForm({ ...propertyForm, title: e.target.value })}
                    data-testid="property-title"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                    placeholder="Luxury Beachfront Villa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Location *</label>
                  <select
                    value={propertyForm.location}
                    onChange={(e) => setPropertyForm({ ...propertyForm, location: e.target.value })}
                    data-testid="property-location"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  >
                    <option value="Mumbai">Mumbai</option>
                    <option value="Lonavala">Lonavala</option>
                    <option value="Alibaug">Alibaug</option>
                    <option value="Dubai">Dubai</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Property Type *</label>
                  <select
                    value={propertyForm.property_type}
                    onChange={(e) => setPropertyForm({ ...propertyForm, property_type: e.target.value })}
                    data-testid="property-type"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  >
                    <option value="Villa">Villa</option>
                    <option value="Penthouse">Penthouse</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Bungalow">Bungalow</option>
                    <option value="Mansion">Mansion</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Price *</label>
                  <input
                    type="text"
                    required
                    value={propertyForm.price}
                    onChange={(e) => setPropertyForm({ ...propertyForm, price: e.target.value })}
                    data-testid="property-price"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                    placeholder="₹15 Cr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Bedrooms *</label>
                  <input
                    type="number"
                    required
                    value={propertyForm.bedrooms}
                    onChange={(e) => setPropertyForm({ ...propertyForm, bedrooms: e.target.value })}
                    data-testid="property-bedrooms"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Bathrooms *</label>
                  <input
                    type="number"
                    required
                    value={propertyForm.bathrooms}
                    onChange={(e) => setPropertyForm({ ...propertyForm, bathrooms: e.target.value })}
                    data-testid="property-bathrooms"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Area (sq ft) *</label>
                  <input
                    type="number"
                    required
                    value={propertyForm.area_sqft}
                    onChange={(e) => setPropertyForm({ ...propertyForm, area_sqft: e.target.value })}
                    data-testid="property-area"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Status *</label>
                  <select
                    value={propertyForm.status}
                    onChange={(e) => setPropertyForm({ ...propertyForm, status: e.target.value })}
                    data-testid="property-status"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  >
                    <option value="available">Available</option>
                    <option value="sold">Sold</option>
                    <option value="reserved">Reserved</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-primary mb-2">Description *</label>
                <textarea
                  required
                  rows="4"
                  value={propertyForm.description}
                  onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                  data-testid="property-description"
                  className="w-full border border-primary/20 p-3 focus:border-primary focus:outline-none resize-none"
                  placeholder="Describe the property..."
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-primary mb-2">Features (comma-separated)</label>
                <input
                  type="text"
                  value={propertyForm.features}
                  onChange={(e) => setPropertyForm({ ...propertyForm, features: e.target.value })}
                  data-testid="property-features"
                  className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  placeholder="Sea View, Private Pool, Garden, Gym"
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-primary mb-2">Image URLs (comma-separated)</label>
                <input
                  type="text"
                  value={propertyForm.images}
                  onChange={(e) => setPropertyForm({ ...propertyForm, images: e.target.value })}
                  data-testid="property-images"
                  className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                />
              </div>

              <div className="mt-6 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={propertyForm.featured}
                  onChange={(e) => setPropertyForm({ ...propertyForm, featured: e.target.checked })}
                  data-testid="property-featured"
                  className="w-5 h-5"
                />
                <label className="text-sm font-medium text-primary">Featured Property</label>
              </div>

              <button
                type="submit"
                data-testid="submit-property"
                className="mt-8 bg-primary text-primary-foreground hover:bg-[#1A3F30] px-8 py-4 font-manrope tracking-wide uppercase text-xs transition-all duration-300 flex items-center gap-2"
              >
                <Plus size={16} />
                Add Property
              </button>
            </form>
          </motion.div>
        )}

        {/* Blog Form */}
        {activeTab === 'blog' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <form onSubmit={handleBlogSubmit} className="bg-white p-8 border border-muted" data-testid="blog-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Blog Title *</label>
                  <input
                    type="text"
                    required
                    value={blogForm.title}
                    onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                    data-testid="blog-title"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                    placeholder="Mumbai Real Estate Trends 2025"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Category *</label>
                  <select
                    value={blogForm.category}
                    onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                    data-testid="blog-category"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  >
                    <option value="Market Insights">Market Insights</option>
                    <option value="Location Spotlight">Location Spotlight</option>
                    <option value="Investment">Investment</option>
                    <option value="Legal & Compliance">Legal & Compliance</option>
                    <option value="International Markets">International Markets</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Excerpt *</label>
                  <textarea
                    required
                    rows="3"
                    value={blogForm.excerpt}
                    onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                    data-testid="blog-excerpt"
                    className="w-full border border-primary/20 p-3 focus:border-primary focus:outline-none resize-none"
                    placeholder="Short description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Content *</label>
                  <textarea
                    required
                    rows="8"
                    value={blogForm.content}
                    onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                    data-testid="blog-content"
                    className="w-full border border-primary/20 p-3 focus:border-primary focus:outline-none resize-none"
                    placeholder="Full article content..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Image URL</label>
                  <input
                    type="text"
                    value={blogForm.image_url}
                    onChange={(e) => setBlogForm({ ...blogForm, image_url: e.target.value })}
                    data-testid="blog-image"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Author</label>
                  <input
                    type="text"
                    value={blogForm.author}
                    onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                    data-testid="blog-author"
                    className="w-full border-b border-primary/20 py-3 focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="submit-blog"
                className="mt-8 bg-primary text-primary-foreground hover:bg-[#1A3F30] px-8 py-4 font-manrope tracking-wide uppercase text-xs transition-all duration-300 flex items-center gap-2"
              >
                <Plus size={16} />
                Publish Blog Post
              </button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Admin;
